import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ActiveUsersChartProps {
  data: {
    time_series: Array<{
      date?: string;
      week_start?: string;
      month?: string;
      active_users: number;
    }>;
    total_unique_users: number;
    granularity: string;
  };
  loading?: boolean;
}

export const ActiveUsersChart: React.FC<ActiveUsersChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="chart-container loading">
        <div className="chart-skeleton"></div>
      </div>
    );
  }

  if (!data || !data.time_series || data.time_series.length === 0) {
    return (
      <div className="chart-container">
        <h3>Active Users Over Time</h3>
        <p className="no-data">No data available for this date range</p>
      </div>
    );
  }

  // Prepare labels based on granularity
  const labels = data.time_series.map(item => {
    if (item.date) return item.date;
    if (item.week_start) return `Week of ${item.week_start}`;
    if (item.month) return item.month;
    return 'Unknown';
  });

  const activeUserCounts = data.time_series.map(item => item.active_users);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Active Users',
        data: activeUserCounts,
        borderColor: '#4A90E2',
        backgroundColor: 'rgba(74, 144, 226, 0.1)',
        borderWidth: 2,
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#4A90E2',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14
        },
        bodyFont: {
          size: 13
        },
        callbacks: {
          label: function(context: any) {
            return `Active Users: ${context.parsed.y.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        },
        ticks: {
          callback: function(value: any) {
            return value.toLocaleString();
          }
        }
      },
      x: {
        grid: {
          display: false
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45
        }
      }
    }
  };

  const granularityLabel = data.granularity === 'daily' ? 'Daily' :
                          data.granularity === 'weekly' ? 'Weekly' :
                          'Monthly';

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h3>{granularityLabel} Active Users</h3>
        <span className="chart-subtitle">
          {data.total_unique_users.toLocaleString()} total unique users
        </span>
      </div>
      <div className="chart-wrapper" style={{ height: '300px' }}>
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default ActiveUsersChart;
